/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, DoCheck, ElementRef, EventEmitter, Input,
  OnDestroy, OnInit, Output, signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { LikeService } from '../../../../services/like.service';
import { openLikeList } from '../like-list/community-like-list-dialog.component';

const PREVIEW_COUNT = 3;

// "Liked by Dennis L. and 4 others" with an avatar stack; clicking it lists everyone who liked the item
// (LIKERS_OPTIONS.html, option A). The feed API only returns like counts, so the first few likers are fetched
// once the item scrolls into view. `compact` is just the count as a link, for replies and comments.
@Component({
  selector: 'ut-community-likers',
  templateUrl: './community-likers.component.html',
  styleUrls: ['./community-likers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityLikersComponent implements OnInit, DoCheck, AfterViewInit, OnDestroy {
  @Input({ required: true }) likeable: any;
  // The signed-in user, or null when logged out.
  @Input({ required: true }) user: any;
  // "No likes yet" instead of nothing when there are no likes.
  @Input() showEmpty = false;
  @Input() compact = false;
  // Opens the like list itself unless a parent listens.
  @Output() showAll = new EventEmitter<void>();

  likers = signal<any[]>([]);

  private observer: IntersectionObserver | undefined;
  // Last like state seen, to notice the Like button changing it.
  private seen: { count: number; liked: boolean } | undefined;

  constructor(
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef,
    private likeService: LikeService,
    private el: ElementRef<HTMLElement>,
    private dialogService: DialogService,
  ) {}

  // The Like button mutates likeable.likes in place when the request returns, which OnPush doesn't see, and
  // announces it on LikeService.changed.
  ngOnInit(): void {
    this.likeService.changed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((likeable) => {
      if (likeable === this.likeable || (likeable.id === this.likeable.id && likeable.type === this.likeable.type)) {
        this.ngDoCheck();
      }
    });
  }

  // Compare with the last like state seen: add or remove the current user and re-render.
  ngDoCheck(): void {
    const now = { count: this.count, liked: !!this.likeable.likes?.liked_by_current_user };
    if (this.seen && (now.count !== this.seen.count || now.liked !== this.seen.liked)) {
      if (this.user && now.liked !== this.seen.liked) {
        const others = this.likers().filter((u) => u.id !== this.user.id);
        this.likers.set(now.liked ? [this.user, ...others] : others);
      }
      this.cdr.markForCheck();
    }
    this.seen = now;
  }

  ngAfterViewInit(): void {
    if (!this.count || this.compact) return;
    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.observer.disconnect();
      this.likeService.likedUsers(this.likeable, { page: 1, per_page: PREVIEW_COUNT })
        .then((res) => this.likers.set(res.data || []));
    }, { rootMargin: '200px' });
    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  get count(): number {
    return this.likeable.likes?.count || 0;
  }

  get preview(): any[] {
    return this.likers().slice(0, PREVIEW_COUNT);
  }

  get leadName(): string {
    const lead = this.likers()[0];
    if (!lead) return '';
    return lead.id === this.user?.id ? 'you' : lead.name;
  }

  get onlyYou(): boolean {
    return this.count === 1 && this.leadName === 'you';
  }

  open(): void {
    if (this.showAll.observed) {
      this.showAll.emit();
    } else {
      openLikeList(this.dialogService, this.likeable, this.user);
    }
  }

  get othersCount(): number {
    return Math.max(this.count - 1, 0);
  }
}
